/**
 * UTM link builder for Share Studio.
 *
 * Taxonomy:
 *   utm_source  — platform (instagram | facebook | qr | link)
 *   utm_medium  — placement (social | story | bio | post | print)
 *   utm_campaign — producer slug (stable owner)
 *   utm_content  — entity identifier (event id/slug, product slug, etc.)
 *   utm_term     — template key (t1 | t2 | t3) for A/B comparison
 */

import type { ShareTarget, SharePlatform, ShareFormat, TemplateKey } from './ShareTarget';

const BASE_URL = 'https://www.farmly.se';

/**
 * Entity path for UTM links.
 * Activities use the /share/ proxy so social bots receive proper OG metadata.
 * Other entities use direct paths.
 */
function entityPath(target: ShareTarget): string {
  switch (target.type) {
    case 'activity':
      return `/share/event/${target.id}`;
    case 'producer':
      return `/share/producer/${target.slug || target.id}`;
    case 'shop':
      return `/shop/${target.slug || target.id}`;
    case 'product':
      return `/product/${target.slug || target.id}`;
  }
}

function inferMedium(platform: SharePlatform, format?: ShareFormat): string {
  if (platform === 'qr') return 'print';
  if (platform === 'bio') return 'bio';
  if (format === 'ig-story') return 'story';
  return 'social';
}

export function buildShareUtmUrl(
  target: ShareTarget,
  platform: SharePlatform,
  opts?: { format?: ShareFormat; templateKey?: TemplateKey },
): string {
  const params = new URLSearchParams({
    utm_source: platform === 'bio' ? 'instagram' : platform,
    utm_medium: inferMedium(platform, opts?.format),
    utm_campaign: target.producerSlug || 'unknown',
    utm_content: target.slug || target.id,
  });
  if (opts?.templateKey) {
    params.set('utm_term', opts.templateKey);
  }
  return `${BASE_URL}${entityPath(target)}?${params.toString()}`;
}
